<template>
  <div class="product-imei-revenue-report-filter">
    <b-row class="mb-3">
      <b-col>
        <div
          class="d-flex align-items-center flex-wrap"
          style="gap: 10px"
        >
          <b-button
            variant="primary"
            size="sm"
            @click="filterDrawerVisible = true"
            class="mb-2 d-flex align-items-center filter-open-btn"
            style="gap: 6px"
          >
            <b-icon
              icon="filter"
              style="flex-shrink: 0"
            ></b-icon>
            <span>Bộ lọc</span>
            <b-badge
              v-if="activeFiltersCount > 0"
              variant="white"
              class="rounded-circle"
            >
              {{ activeFiltersCount }}
            </b-badge>
          </b-button>
          <div
            class="d-flex align-items-center flex-wrap"
            v-if="activeFiltersList.length > 0"
          >
            <b-badge
              v-for="filter in activeFiltersList"
              :key="filter.key"
              variant="light"
              class="mr-2 mb-2 filter-badge border border-dark text-dark"
              style="min-height: 37px"
            >
              <span
                class="filter-badge-label"
                v-ellipsis-tooltip="(isOverflow) => (filter._showTooltip = isOverflow)"
                v-b-tooltip.hover="filter._showTooltip ? filter.label : ''"
              >
                {{ filter.label }}
              </span>
              <b-button
                v-if="filter.clearable"
                variant="secondary"
                class="ml-4 rounded-circle btn btn-xs btn-icon"
                @click="removeFilter(filter.key)"
                style="text-decoration: none; font-size: 14px"
              >
                <b-icon icon="x"></b-icon>
              </b-button>
            </b-badge>
          </div>
        </div>
      </b-col>

      <b-col class="col-auto">
        <div class="d-flex justify-content-end align-items-center flex-nowrap">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="clearFilters"
            class="mr-2 mb-2 text-nowrap"
            :disabled="loading || activeFiltersCount === 0"
          >
            <i class="fi fi-ts-filter-slash"></i>
            Xoá bộ lọc
          </b-button>
          <b-button
            variant="outline-success"
            size="sm"
            @click="$emit('export')"
            class="mr-2 mb-2 text-nowrap"
            :disabled="loading"
            v-if="hasExportPermission"
          >
            <i class="fi fi-ts-file-excel"></i>
            Xuất Excel
          </b-button>
          <b-button
            variant="outline-info"
            size="sm"
            @click="$emit('refresh')"
            class="mb-2 text-nowrap"
            :disabled="loading"
          >
            <i class="flaticon2-refresh"></i>
            Làm mới
          </b-button>
        </div>
      </b-col>
    </b-row>

    <Drawer
      v-if="filterDrawerVisible"
      id="product-imei-revenue-filter-drawer"
      :maskClosable="true"
      :zIndex="1002"
      @close="filterDrawerVisible = false"
      align="right"
      :closeable="true"
    >
      <template #header>
        <div class="d-flex w-100 align-items-center justify-content-between">
          <h5 class="mb-0 text-dark">
            <i class="flaticon2-funnel text-primary mr-2"></i>
            Bộ lọc báo cáo
            <b-badge
              v-if="activeFiltersCount > 0"
              variant="primary"
              class="ml-2"
            >
              {{ activeFiltersCount }}
            </b-badge>
          </h5>
        </div>
      </template>

      <div class="filter-section mb-4">
        <h6 class="filter-section-title">Khoảng thời gian</h6>

        <b-form-group
          label="Chọn nhanh:"
          label-size="sm"
          class="mb-3"
        >
          <div class="d-flex flex-wrap">
            <b-button
              v-for="preset in datePresets"
              :key="preset.value"
              :variant="selectedPreset === preset.value ? 'primary' : 'outline-secondary'"
              @click="applyDatePreset(preset.value)"
              size="sm"
              class="mr-2 mb-2 flex-fill"
            >
              {{ preset.label }}
            </b-button>
          </div>
        </b-form-group>

        <b-form-group
          label-size="sm"
          class="mb-2"
        >
          <template #label> Từ ngày <span class="text-danger">*</span> </template>
          <date-picker
            placeholder="Chọn ngày bắt đầu"
            class="form-control-sm"
            :class="{ 'border border-danger rounded': dateError && !filterData.startDate }"
            :config="dpConfigs.date"
            v-model="filterData.startDate"
            @input="onDateChange"
          ></date-picker>
          <small
            v-if="dateError && !filterData.startDate"
            class="text-danger"
            >Vui lòng chọn ngày bắt đầu</small
          >
        </b-form-group>

        <b-form-group
          label-size="sm"
          class="mb-3"
        >
          <template #label> Đến ngày <span class="text-danger">*</span> </template>
          <date-picker
            placeholder="Chọn ngày kết thúc"
            class="form-control-sm"
            :class="{ 'border border-danger rounded': dateError && !filterData.endDate }"
            :config="dpConfigs.date"
            v-model="filterData.endDate"
            @input="onDateChange"
          ></date-picker>
          <small
            v-if="dateError && !filterData.endDate"
            class="text-danger"
            >Vui lòng chọn ngày kết thúc</small
          >
        </b-form-group>
      </div>

      <div class="filter-section mb-4">
        <h6 class="filter-section-title">Chi nhánh</h6>
        <b-form-group>
          <b-form-select
            class="select-style"
            v-model="filterData.storeId"
            :options="storeOptions"
            size="sm"
          ></b-form-select>
        </b-form-group>
      </div>

      <div class="filter-section mb-4">
        <h6 class="filter-section-title">Loại hoá đơn</h6>
        <b-form-group>
          <b-form-select
            class="select-style"
            v-model="filterData.billType"
            :options="options"
            size="sm"
          ></b-form-select>
        </b-form-group>
      </div>

      <div class="filter-section mb-4">
        <h6 class="filter-section-title">Danh mục sản phẩm</h6>
        <b-form-group class="mb-0">
          <treeselect
            :options="categoryTreeOptions"
            :multiple="true"
            :searchable="true"
            :match-keys="['id', 'label']"
            placeholder="Chọn danh mục"
            :clearable="true"
            :show-count="true"
            open-direction="bottom"
            v-model="filterData.categoryIds"
          />
          <small class="text-muted d-block mt-1">
            Hệ thống lọc theo danh mục đã chọn và toàn bộ danh mục con.
          </small>
        </b-form-group>
      </div>

      <div class="filter-section mb-4">
        <h6 class="filter-section-title">Sản phẩm</h6>
        <b-form-group class="mb-0">
          <Autosuggest
            :model="searchProduct"
            :suggestions="productSuggestions"
            placeholder="mã, tên, mã vạch sản phẩm"
            :limit="10"
            :show-clear="true"
            suggestion-name="productName"
            @select="onSelectedProduct"
            @change="onInputChangeProduct"
          />
          <small class="text-muted d-block mt-1"> Gõ và chọn một sản phẩm để chỉ xem các dòng IMEI thuộc sản phẩm đó. </small>
        </b-form-group>
      </div>

      <div class="filter-section mb-4">
        <h6 class="filter-section-title">Mã IMEI / Serial</h6>
        <b-form-group>
          <b-form-input
            v-model="filterData.imeiCode"
            placeholder="Nhập mã IMEI hoặc Serial..."
            size="sm"
          />
        </b-form-group>
      </div>

      <div class="filter-section mb-4">
        <h6 class="filter-section-title">Mã hoá đơn</h6>
        <b-form-group>
          <b-form-input
            v-model="filterData.billNumber"
            placeholder="Nhập mã hoá đơn..."
            size="sm"
            type="number"
          />
        </b-form-group>
      </div>

      <template #footer>
        <div class="d-flex justify-content-between w-100">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="clearFilters"
            :disabled="activeFiltersCount === 0"
          >
            <i class="fi fi-ts-filter-slash"></i>
            Xóa bộ lọc
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="applyFiltersAndClose"
            class="ml-2"
          >
            <i class="flaticon2-check-mark"></i>
            Áp dụng
          </b-button>
        </div>
      </template>
    </Drawer>
  </div>
</template>

<script>
import moment from 'moment'
import datePicker from 'vue-bootstrap-datetimepicker'
import timeUtils from '@/utils/date'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue'

const Drawer = () => import(/* webpackChunkName: "Drawer" */ '@/components/common/Drawer')

export default {
  name: 'ProductImeiRevenueReportFilter',
  components: {
    datePicker,
    Drawer,
    Treeselect,
    Autosuggest,
  },
  props: {
    initialFilter: {
      type: Object,
      default: () => ({}),
    },
    stores: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasExportPermission: {
      type: Boolean,
      default: false,
    },
    /** Cây danh mục do trang cha load API */
    categoryTreeOptions: {
      type: Array,
      default: () => [],
    },
    /** Gợi ý sản phẩm (search) do trang cha load theo @product-search */
    productSuggestions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filterDrawerVisible: false,
      dateError: false,
      searchProduct: '',
      filterData: {
        startDate: '',
        endDate: '',
        storeId: null,
        billType: null,
        imeiCode: '',
        billNumber: '',
        categoryIds: [],
        productId: null,
      },
      selectedPreset: 'last30days',
      dpConfigs: timeUtils.DP_CONFIG,
      datePresets: [
        { label: '7 ngày trước', value: 'last7days' },
        { label: 'Tuần trước', value: 'lastWeek' },
        { label: '30 ngày trước', value: 'last30days' },
        { label: 'Tháng trước', value: 'lastMonth' },
        { label: 'Tùy chọn', value: 'custom' },
      ],
    }
  },
  computed: {
    storeOptions() {
      const options = [{ value: null, text: 'Tất cả chi nhánh' }]
      this.stores.forEach((store) => {
        options.push({
          value: store.id,
          text: store.shortName || store.name,
        })
      })
      return options
    },
    activeFiltersCount() {
      let count = 0
      if (this.filterData.storeId) count++
      if (this.filterData.billType) count++
      if (this.filterData.categoryIds && this.filterData.categoryIds.length) count++
      if (this.filterData.productId) count++
      if (this.filterData.imeiCode) count++
      if (this.filterData.billNumber) count++
      return count
    },
    activeFiltersList() {
      const filters = []

      if (this.filterData.startDate && this.filterData.endDate) {
        filters.push({
          key: 'dateRange',
          label: `Ngày: ${this.filterData.startDate} - ${this.filterData.endDate}`,
          value: 'dateRange',
          clearable: false,
        })
      }

      if (this.filterData.storeId) {
        const store = this.stores.find((s) => s.id === this.filterData.storeId)
        filters.push({
          key: 'storeId',
          label: `Chi nhánh: ${store ? store.shortName || store.name : this.filterData.storeId}`,
          value: this.filterData.storeId,
          clearable: true,
        })
      }

      if (this.filterData.billType) {
        const billTypeOption = this.options.find(
          (o) => String(o.value) === String(this.filterData.billType),
        )
        filters.push({
          key: 'billType',
          label: `Loại HĐ: ${billTypeOption ? billTypeOption.text : this.filterData.billType}`,
          value: this.filterData.billType,
          clearable: true,
        })
      }

      if (this.filterData.categoryIds && this.filterData.categoryIds.length) {
        const n = this.filterData.categoryIds.length
        filters.push({
          key: 'categoryIds',
          label: `Danh mục: ${n} đã chọn`,
          value: this.filterData.categoryIds,
          clearable: true,
        })
      }

      if (this.filterData.productId) {
        filters.push({
          key: 'productId',
          label: `Sản phẩm: ${this.searchProduct || this.filterData.productId}`,
          value: this.filterData.productId,
          clearable: true,
        })
      }

      if (this.filterData.imeiCode) {
        filters.push({
          key: 'imeiCode',
          label: `IMEI: ${this.filterData.imeiCode}`,
          value: this.filterData.imeiCode,
          clearable: true,
        })
      }

      if (this.filterData.billNumber) {
        filters.push({
          key: 'billNumber',
          label: `Mã HĐ: ${this.filterData.billNumber}`,
          value: this.filterData.billNumber,
          clearable: true,
        })
      }

      return filters
    },
  },
  created() {
    this.filterData = { ...this.filterData, ...this.initialFilter }
    if (!Array.isArray(this.filterData.categoryIds)) {
      this.filterData.categoryIds = []
    }
    if (!this.filterData.startDate || !this.filterData.endDate) {
      this.applyDatePreset('last30days')
    }
  },
  methods: {
    applyDatePreset(preset) {
      this.selectedPreset = preset
      const today = moment()

      switch (preset) {
        case 'last7days':
          this.filterData.startDate = today.clone().subtract(6, 'days').format('DD/MM/YYYY')
          this.filterData.endDate = today.format('DD/MM/YYYY')
          break
        case 'lastWeek':
          this.filterData.startDate = today
            .clone()
            .subtract(1, 'week')
            .startOf('week')
            .format('DD/MM/YYYY')
          this.filterData.endDate = today
            .clone()
            .subtract(1, 'week')
            .endOf('week')
            .format('DD/MM/YYYY')
          break
        case 'last30days':
          this.filterData.startDate = today.clone().subtract(29, 'days').format('DD/MM/YYYY')
          this.filterData.endDate = today.format('DD/MM/YYYY')
          break
        case 'lastMonth':
          this.filterData.startDate = today
            .clone()
            .subtract(1, 'month')
            .startOf('month')
            .format('DD/MM/YYYY')
          this.filterData.endDate = today
            .clone()
            .subtract(1, 'month')
            .endOf('month')
            .format('DD/MM/YYYY')
          break
        case 'custom':
          break
      }
    },
    onDateChange() {
      this.selectedPreset = 'custom'
    },
    removeFilter(filterKey) {
      switch (filterKey) {
        case 'storeId':
          this.filterData.storeId = null
          break
        case 'billType':
          this.filterData.billType = null
          break
        case 'imeiCode':
          this.filterData.imeiCode = ''
          break
        case 'billNumber':
          this.filterData.billNumber = ''
          break
        case 'categoryIds':
          this.filterData.categoryIds = []
          break
        case 'productId':
          this.filterData.productId = null
          this.searchProduct = ''
          this.$emit('product-search', '')
          break
      }
      this.emitFilter()
    },
    clearFilters() {
      this.dateError = false
      this.filterData = {
        startDate: '',
        endDate: '',
        storeId: null,
        billType: null,
        imeiCode: '',
        billNumber: '',
        categoryIds: [],
        productId: null,
      }
      this.searchProduct = ''
      this.$emit('product-search', '')
      this.applyDatePreset('last30days')
      this.emitFilter()
      this.filterDrawerVisible = false
    },
    applyFiltersAndClose() {
      if (!this.filterData.startDate || !this.filterData.endDate) {
        this.dateError = true
        return
      }
      this.dateError = false
      this.emitFilter()
      this.filterDrawerVisible = false
    },
    emitFilter() {
      this.$emit('filter', { ...this.filterData })
    },
    onSelectedProduct(option) {
      if (!option || !option.item) return
      const item = option.item
      this.searchProduct = item.productName || ''
      this.filterData.productId = item.id != null ? item.id : null
    },
    onInputChangeProduct(text) {
      const t = text != null ? String(text) : ''
      this.searchProduct = t
      this.filterData.productId = null
      this.$emit('product-search', t)
    },
  },
}
</script>

<style scoped>
.filter-open-btn {
  min-height: 35px;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  max-width: 235px;
}

.filter-badge-label {
  display: inline-block;
  max-width: 175px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.filter-section {
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 1rem;
}

.filter-section:last-child {
  border-bottom: none;
}

.filter-section-title {
  font-size: 1.075rem;
  font-weight: 600;
  color: #3f4254;
  margin-bottom: 0.75rem;
}
</style>
